import { ChevronRight, Close } from '@mui/icons-material';
import { Avatar, Button, Fab, IconButton, useTheme } from '@mui/material';
import { useFormikContext } from 'formik';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
    return centerCrop(
        makeAspectCrop(
            {
                unit: '%',
                width: 90,
            },
            aspect,
            mediaWidth,
            mediaHeight
        ),
        mediaWidth,
        mediaHeight
    );
}

export default function ProfileImageUpload({
    name,
    placeholder,
    alt,
    setUpdateReady,
}) {
    const [imgArray, setImgArray] = useState([]);
    const [isCropped, setIsCropped] = useState(false);

    const imgRef = useRef(null);
    const previewCanvasRef = useRef(null);
    const theme = useTheme();

    const [upImg, setUpImg] = useState();
    const [crop, setCrop] = useState({
        unit: '%',
        x: 25,
        y: 25,
        width: 50,
        height: 50,
    });
    const [completedCrop, setCompletedCrop] = useState(null);

    const onLoad = useCallback((e) => {
        // imgRef.current = img;
        const { width, height } = e.currentTarget;
        setCrop(centerAspectCrop(width, height, 1));
    }, []);

    useEffect(() => {
        if (
            completedCrop?.width &&
            completedCrop?.height &&
            imgRef.current &&
            previewCanvasRef.current
        ) {
            const image = imgRef.current;
            const canvas = previewCanvasRef.current;
            const eCrop = completedCrop;

            const scaleX = image.naturalWidth / image.width;
            const scaleY = image.naturalHeight / image.height;
            const ctx = canvas.getContext('2d');
            const pixelRatio = window.devicePixelRatio;

            canvas.width = eCrop.width * pixelRatio * scaleX;
            canvas.height = eCrop.height * pixelRatio * scaleY;

            ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
            ctx.imageSmoothingQuality = 'high';

            ctx.drawImage(
                image,
                eCrop.x * scaleX,
                eCrop.y * scaleY,
                eCrop.width * scaleX,
                eCrop.height * scaleY,
                0,
                0,
                eCrop.width * scaleX,
                eCrop.height * scaleY
            );
        }
    }, [completedCrop]);

    function generateDownload(canvas, iCrop) {
        if (!iCrop || !canvas) {
            return;
        }

        canvas.toBlob(
            (blob) => {
                setImgArray([...imgArray, blob]);
            },
            'image/png',
            1
        );
    }

    const { errors, setFieldTouched, touched, setFieldValue, setFieldError } =
        useFormikContext();

    useEffect(() => {
        name && setFieldValue(name, imgArray);
        placeholder && (imgArray[0] = placeholder);
    }, [imgArray, name, setFieldValue, placeholder]);

    const deleteImageUpload = (item) => {
        const newArray = [...imgArray];

        newArray.filter((val) => {
            return val?.name !== item.name;
        });

        setImgArray(
            newArray.filter((val) => {
                return val.name !== item.name;
            })
        );
    };

    return (
        <>
            {!isCropped && (
                <>
                    {!!upImg && (
                        <ReactCrop
                            crop={crop}
                            onChange={(_, c) => setCrop(c)}
                            onComplete={(c) => setCompletedCrop(c)}
                            aspect={1}
                        >
                            <img
                                ref={imgRef}
                                src={upImg}
                                onLoad={onLoad}
                                alt="image crop"
                            />
                        </ReactCrop>
                    )}
                    {!!completedCrop && (
                        <canvas
                            ref={previewCanvasRef}
                            style={{
                                // width: Math.round(completedCrop?.width ?? 0),
                                // height: Math.round(completedCrop?.height ?? 0),

                                border: '1px solid black',
                                objectFit: 'contain',
                                width: completedCrop.width,
                                height: completedCrop.height,
                            }}
                        />
                    )}
                    <br />
                    {!(!completedCrop?.width || !completedCrop?.height) && (
                        <Button
                            variant="outlined"
                            onClick={() => {
                                generateDownload(
                                    previewCanvasRef.current,
                                    completedCrop
                                );
                                setIsCropped(true);
                                setUpdateReady(true);
                            }}
                        >
                            Finish Cropping
                        </Button>
                    )}
                </>
            )}

            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                {imgArray.length > 1 &&
                    imgArray.map((item) => {
                        const isUrl = item.toString().search('http') === 0;
                        return (
                            <>
                                <Avatar
                                    key={item}
                                    alt={alt}
                                    src={
                                        isUrl
                                            ? placeholder
                                            : window.URL.createObjectURL(item)
                                    }
                                    style={{
                                        width: 100,
                                        height: 100,
                                    }}
                                ></Avatar>
                                <br />
                                {!isUrl && (
                                    <IconButton
                                        onClick={() => {
                                            deleteImageUpload(item);
                                            setIsCropped(false);
                                            setUpdateReady(false);
                                        }}
                                        style={{
                                            backgroundColor:
                                                theme.palette.background.paper,

                                            color: theme.palette.text.primary,
                                        }}
                                        size="small"
                                    >
                                        <Close />
                                    </IconButton>
                                )}
                            </>
                        );
                    })}
            </div>

            {imgArray.length < (placeholder ? 2 : 4) && !upImg && (
                <div style={{ position: 'relative' }}>
                    <input
                        onChange={(e) => {
                            const path =
                                document.querySelector('#upload-photo').value;
                            const filetype = path
                                .substring(path.lastIndexOf('.') + 1)
                                .toLowerCase();

                            if (
                                filetype === 'jpg' ||
                                filetype === 'png' ||
                                filetype === 'jpeg'
                            ) {
                                if (e.target.files[0].size < 2097152) {
                                    if (
                                        e.target.files &&
                                        e.target.files.length > 0
                                    ) {
                                        const reader = new FileReader();
                                        reader.addEventListener('load', () =>
                                            setUpImg(reader.result)
                                        );
                                        reader.readAsDataURL(e.target.files[0]);
                                    }
                                } else {
                                    setFieldError(
                                        name,
                                        Math.round(
                                            e.target?.files[0]?.size /
                                                (1024 * 1024)
                                        ) + 'MB image is too large (max 2MB)'
                                    );
                                }
                            } else {
                                setFieldError(name, 'Invalid file type');
                            }
                        }}
                        onBlur={() => name && setFieldTouched(name)}
                        style={{
                            position: 'absolute',
                            opacity: 0,
                            zIndex: 2,
                            marginTop: 20,
                        }}
                        type="file"
                        accept="image/*"
                        name="photo"
                        id="upload-photo"
                    />
                    <label
                        style={{ width: '100%', marginTop: 10 }}
                        htmlFor="upload-photo"
                    >
                        <Button variant="outlined">Upload Image</Button>
                    </label>
                </div>
            )}

            {name && touched[name] && errors[name] && (
                <small
                    className="mb-2 mx-2"
                    style={{ color: theme.palette.error.main }}
                >
                    {errors[name]}
                </small>
            )}

            <Fab
                size="medium"
                style={{ position: 'fixed', bottom: 16, right: 16 }}
                onClick={() => window.scrollTo(0, document.body.scrollHeight)}
            >
                <ChevronRight style={{ transform: 'rotate(90deg)' }} />
            </Fab>
        </>
    );
}
